import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Receipt } from 'lucide-react';
import { formatCurrency } from '@/lib/dashboardData';

// Matches JobInvoicesTab's STATUS_BADGE tones so a status reads the same
// color everywhere it appears.
const STATUS_BADGE = {
  paid: 'bg-[hsl(var(--success))]/15 text-[hsl(var(--success))] border-[hsl(var(--success))]/30',
  overdue:
    'bg-[hsl(var(--destructive))]/15 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30',
  due: 'bg-[hsl(var(--warning))]/15 text-[hsl(var(--warning))] border-[hsl(var(--warning))]/30',
  upcoming: 'bg-[hsl(var(--muted))] text-foreground border-border',
};

/**
 * Wide-screen dashboard pass (2026-09): "unpaid invoices ... visible
 * without scrolling" — the soonest-due unpaid invoices across every job,
 * built from the same flattening the Invoices page itself uses (see
 * dashboardData.buildUnpaidInvoices) so the two never disagree.
 */
export const UnpaidInvoicesCard = ({ invoices = [] }) => {
  const { t } = useTranslation();
  return (
    <Card
      className="border-[hsl(var(--hairline))] bg-[hsl(var(--card))] h-full"
      data-testid="unpaid-invoices-card"
    >
      <CardContent className="p-4 sm:p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <div className="text-[10px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
              {t('dashboard.unpaid_invoices.eyebrow', { defaultValue: 'Billing' })}
            </div>
            <h2 className="mt-0.5 text-base sm:text-lg font-semibold text-foreground">
              {t('dashboard.unpaid_invoices.title', { defaultValue: 'Unpaid Invoices' })}
            </h2>
          </div>
          <Receipt size={16} className="text-[hsl(var(--primary))]/70" />
        </div>

        {invoices.length === 0 ? (
          <div className="py-10 text-center text-sm text-muted-foreground">
            {t('dashboard.unpaid_invoices.empty', { defaultValue: 'Everything is paid up.' })}
          </div>
        ) : (
          <ScrollArea className="max-h-[420px] kc-scroll pr-2">
            <ul className="divide-y divide-border/60" data-testid="unpaid-invoices-list">
              {invoices.map((inv) => (
                <li
                  key={inv.row_key}
                  className="py-2.5 flex items-start justify-between gap-3"
                  data-testid={`unpaid-invoice-item-${inv.row_key}`}
                >
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {inv.job_name}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="truncate">{inv.desc || inv.client}</span>
                      {inv.due ? <span className="shrink-0">· {inv.due}</span> : null}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <div className="text-sm font-semibold tabular-nums text-foreground">
                      {formatCurrency(inv.amount)}
                    </div>
                    <Badge
                      variant="outline"
                      className={`mt-1 uppercase text-[9px] tracking-[0.14em] ${
                        STATUS_BADGE[inv.status] || STATUS_BADGE.upcoming
                      }`}
                    >
                      {inv.status}
                    </Badge>
                  </div>
                </li>
              ))}
            </ul>
          </ScrollArea>
        )}

        <Link
          to="/dashboard/invoices"
          className="mt-3 inline-block text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--primary))] hover:underline"
          data-testid="unpaid-invoices-view-all"
        >
          {t('dashboard.unpaid_invoices.view_all', { defaultValue: 'View all invoices' })}
        </Link>
      </CardContent>
    </Card>
  );
};

export default UnpaidInvoicesCard;
